import {
  Badge,
  Button,
  CloseButton,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActionCableContext } from '..';
import { securedAxiosInstance } from '../api/axios';
import { clearComments } from '../stores/slices/comments/commentsSlice';
import Notification from './icons/Notification';
import EditGoalItem from './MyBox/EditGoalItem';
import GoalComments from './MyBox/GoalComments';

const NotificationBell = () => {
  const cable = useContext(ActionCableContext);
  const { userInfo } = useSelector(state => state.user);
  const [notifications, setNotifications] = useState([]);
  const [goal, setGoal] = useState(null);
  const [openComments, setOpenComments] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const getNotifs = async () => {
      const res = await securedAxiosInstance.get('notifications_users');
      setNotifications(res.data.reverse());
    };

    getNotifs();
  }, []);

  useEffect(() => {
    cable.subscriptions.create(
      {
        channel: 'NotificationsChannel',
        room: userInfo.id,
      },
      {
        received: data => {
          setNotifications(notif => {
            return [data.notifications_user, ...notif];
          });
          toast({
            description: data.notifications_user.notification.text,
            status: 'info',
            duration: 3000,
            isClosable: true,
            position: 'bottom-right',
            variant: 'secondary',
          });
        },
      }
    );
  }, [userInfo.id, cable.subscriptions]);

  const closeNotification = async id => {
    setNotifications(notifications => {
      return notifications.filter(notif => notif.id !== id);
    });
    await securedAxiosInstance.put(`/update_notification/${id}`, {
      notifications_user: { read: true },
    });
  };

  const showGoal = async goalId => {
    setIsLoading(true);
    const { data } = await securedAxiosInstance.get(`/goals/${goalId}`);
    setGoal(data);
    setOpenComments(true);
    setIsLoading(false);
    setSelectedNotif(null);
  };

  const goToNotif = async ({ refType, refId, id, onClose }) => {
    if (refType === 'goal') {
      dispatch(clearComments());
      await showGoal(refId);
      onClose();
    }
    if (refType === 'reminder') navigate('reminders');
    if (refType === 'user') {
      if (userInfo.role === 'coach') {
        navigate('settings/organisation/user-access');
      }
      if (userInfo.role === 'employer') {
        navigate('settings/organisation/employees');
      }
    }
    if (refType === 'organisation') navigate('settings/organisations');
    onClose();
  };

  return (
    <React.Fragment>
      <Menu closeOnSelect={false}>
        {({ onClose }) => (
          <React.Fragment>
            <MenuButton
              as={IconButton}
              bg={'white'}
              rounded={'full'}
              position={'relative'}
              icon={
                <React.Fragment>
                  <Icon as={Notification} color="lightGray.500" />
                  <Badge
                    position={'absolute'}
                    top={0}
                    right={'-1'}
                    bg={'primary'}
                    color={'white'}
                    rounded={'full'}
                    fontSize={'12px'}
                    fontWeight={'normal'}
                  >
                    {notifications.length}
                  </Badge>
                </React.Fragment>
              }
            />
            <MenuList overflowY={'auto'} maxH={'lg'} maxW={'sm'} zIndex={'50'}>
              <HStack justifyContent={'flex-end'} pb={'2'}>
                <Button
                  color="primary"
                  variant={'ghost'}
                  size="xs"
                  fontSize={'14px'}
                  onClick={() => {
                    navigate('/dashboard/notifications');
                    onClose();
                  }}
                >
                  View All
                </Button>
              </HStack>
              {notifications.length > 0 ? (
                notifications.map(notif => (
                  <MenuItem
                    key={notif.id}
                    as={'div'}
                    justifyContent={'space-between'}
                    gap={'2'}
                    onClick={() => {
                      setSelectedNotif(notif.id);
                      goToNotif({
                        refType: notif.notification.ref_type,
                        refId: notif.notification.ref_id,
                        id: notif.notification.id,
                        onClose,
                      });
                    }}
                  >
                    <HStack>
                      {isLoading && selectedNotif === notif.id ? (
                        <Spinner color="primary" />
                      ) : null}
                      <Text>{notif.notification.text}</Text>
                    </HStack>
                    <CloseButton
                      bg={'primaryDark'}
                      _hover={{
                        bg: 'primaryDark',
                      }}
                      color={'white'}
                      rounded={'full'}
                      w={'5'}
                      h={'5'}
                      fontSize={'8px'}
                      onClick={e => {
                        e.stopPropagation();
                        closeNotification(notif.id);
                      }}
                    />
                  </MenuItem>
                ))
              ) : (
                <MenuItem>No notifications yet.</MenuItem>
              )}
            </MenuList>
          </React.Fragment>
        )}
      </Menu>

      {isOpen && (
        <EditGoalItem
          isOpen={isOpen}
          onClose={onClose}
          openComments={openComments}
          category={goal.goal_category}
          goal={goal}
        />
      )}

      {openComments && (
        <GoalComments
          setOpenComments={setOpenComments}
          goal={goal}
          openEdit={onOpen}
          goalCategoryId={goal.goal_category.id}
        />
      )}
    </React.Fragment>
  );
};

export default NotificationBell;
